import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { CHECK_STATUSES, type CheckStatus } from '@accounting/shared';
import { useChecks, useSetCheckCleared, useVoidCheck } from '../../hooks/useChecks';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/checks/')({
  component: ChecksPage,
});

function statusTag(s: CheckStatus) {
  if (s === 'cleared') return <span className="tag ok">cleared</span>;
  if (s === 'voided') return <span className="tag bad">voided</span>;
  if (s === 'to_print') return <span className="tag">to print</span>;
  if (s === 'printing') return <span className="tag warn">printing</span>;
  return <span className="tag warn">outstanding</span>;
}

function ChecksPage() {
  const companyId = useActiveCompanyId();
  const [status, setStatus] = useState<CheckStatus | ''>('');
  const { data: checks = [], isLoading, error } = useChecks(companyId, {
    status: status || undefined,
  });
  const { data: accounts = [] } = useAccounts(companyId);
  const acctName = new Map(accounts.map((a) => [a.id, `${a.code} — ${a.name}`]));
  const clear = useSetCheckCleared(companyId);
  const voidCheck = useVoidCheck(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Checks</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Checks</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Check register</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13 }}>
            Status{' '}
            <select value={status} onChange={(e) => setStatus(e.target.value as CheckStatus | '')}>
              <option value="">All</option>
              {CHECK_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <Link to="/checks/print-queue" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Print queue
          </Link>
          <Link to="/checks/new" className="btn-primary" style={{ textDecoration: 'none' }}>
            + Write a check
          </Link>
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load checks.</p>}

      {!isLoading && checks.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No checks yet.</p>
          <Link to="/checks/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Write your first check
          </Link>
        </div>
      )}

      {checks.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Payee</th>
              <th>Account</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c) => (
              <tr key={c.id} style={{ opacity: c.status === 'voided' ? 0.6 : 1 }}>
                <td>{c.checkNumber ?? <span className="muted">—</span>}</td>
                <td>{formatAccountingDate(c.checkDate)}</td>
                <td>{c.payeeName}</td>
                <td className="muted">{acctName.get(c.bankAccountId) ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
                <td>{statusTag(c.status)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link to="/checks/$id" params={{ id: c.id }} className="btn-secondary" style={{ textDecoration: 'none', marginRight: 6 }}>
                    View
                  </Link>
                  {/* A check that hasn't finished printing can't have cleared the
                      bank, and its number isn't settled — neither action applies. */}
                  {c.status !== 'voided' && c.status !== 'to_print' && c.status !== 'printing' && (
                    <>
                      <button
                        className="btn-secondary"
                        style={{ marginRight: 6 }}
                        disabled={clear.isPending}
                        onClick={() => clear.mutate({ id: c.id, cleared: c.status !== 'cleared' })}
                      >
                        {c.status === 'cleared' ? 'Un-clear' : 'Mark cleared'}
                      </button>
                      <button
                        className="btn-danger"
                        disabled={voidCheck.isPending}
                        onClick={() => {
                          const reason = prompt(`Void check #${c.checkNumber}? Reason:`);
                          if (reason && reason.trim()) voidCheck.mutate({ id: c.id, reason: reason.trim() });
                        }}
                      >
                        Void
                      </button>
                    </>
                  )}
                  {c.status === 'to_print' && (
                    <button
                      className="btn-danger"
                      disabled={voidCheck.isPending}
                      onClick={() => {
                        const reason = prompt('Cancel this queued check? Reason:');
                        if (reason && reason.trim()) voidCheck.mutate({ id: c.id, reason: reason.trim() });
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
